/**
 * Curated pin for ad landing pages.
 *
 * When a marketing URL carries `?first=<handle>` (optionally `&variant=<id>`),
 * move that product's collection card to the front of the grid and — when a
 * variant is given — show that variant's color image and carry `?variant=` on
 * the card link, by driving the card's own slideshow. No markup is added.
 *
 * Dormant by default: with no `?first=` param nothing runs (no DOM touch, no
 * network), so organic browsing is byte-for-byte unchanged.
 *
 * If the product is already in the rendered grid it is reordered (no fetch).
 * Otherwise — the collection paginates and the product sits on a later page —
 * its card is fetched from that page (Section Rendering API) and prepended, so
 * any advertised product pins regardless of its position in the collection.
 * A product not in the collection at all is a graceful no-op.
 */

const PRODUCT_LINK = 'a.product-card__link, a[ref="cardGalleryLink"], a[ref="productTitleLink"]';
const productCache = {};

const params = new URL(location.href).searchParams;
const firstParam = params.get('first');
if (firstParam) {
  init(firstParam, params.get('variant'));
}

/**
 * @param {string} firstRaw - raw `first` param (handle or product path/URL).
 * @param {string | null} variantRaw - raw `variant` param.
 */
async function init(firstRaw, variantRaw) {
  const handle = handleFromParam(firstRaw);
  if (!handle) return;
  const variantId = normalizeVariantId(variantRaw);

  // Wait for the card component to register so the grid has finished hydrating
  // before we mutate it (it can be re-rendered during upgrade).
  await customElements.whenDefined('product-card').catch(() => {});

  const grid = document.querySelector('[ref="grid"]') || document.querySelector('.product-grid');
  if (!grid) return;

  let card = findCardByHandle(grid, handle);
  let fetched = false;
  if (card) {
    // Already rendered → reorder to the front (no fetch).
    pinCard(grid, card);
  } else {
    // Not in the rendered page → reserve slot #1 with a skeleton, fetch the
    // product's bare card, then prepend it so the grid doesn't lurch.
    const skeleton = buildSkeleton(grid);
    grid.prepend(skeleton);
    card = await fetchCardView(handle, variantId).catch(() => null);
    if (skeleton.parentNode) skeleton.remove(); // always clear the placeholder
    if (!card) return; // not in this collection / fetch failed → graceful no-op
    matchGridRatio(grid, card); // copy the live image-ratio so the swap doesn't shift
    const liveGrid = document.querySelector('[ref="grid"]') || document.querySelector('.product-grid') || grid;
    liveGrid.prepend(card);
    markPinned(liveGrid, card);
    fetched = true;
  }

  // A fetched card was rendered server-side with `&variant=`, so its image and
  // link are already correct — running applyVariant would only trigger a
  // redundant /products/<handle>.js fetch that swaps in the same image a beat
  // later. Only the reordered (page-1) card, rendered with its default variant,
  // needs the client-side swap.
  if (variantId && !fetched) {
    try {
      await applyVariant(card, handle, variantId);
    } catch (e) {
      // Image swap is non-blocking: keep the default image, still carry the link param.
      try {
        setVariantLink(card, variantId);
      } catch (_) {
        /* no-op */
      }
    }
  }

  if (paginationMayInject()) watchForDuplicate(grid, handle, card);
}

/* --------------------------------- parsing -------------------------------- */

/**
 * Resolve a product handle from the `first` param: bare handle,
 * `/products/<handle>`, locale-prefixed, or a full URL — with optional query/hash.
 * @param {string} raw
 * @returns {string | null}
 */
function handleFromParam(raw) {
  try {
    let s = decodeURIComponent(String(raw)).trim();
    s = s.split('?')[0].split('#')[0];
    const m = s.match(/\/products\/([^/]+)/);
    if (m) return m[1];
    const seg = s.replace(/\/+$/, '').split('/').pop();
    return seg || null;
  } catch (e) {
    return null;
  }
}

/**
 * Extract a handle from a product link href.
 * @param {string | null} href
 * @returns {string | null}
 */
function handleFromHref(href) {
  if (!href) return null;
  try {
    const m = new URL(href, location.origin).pathname.match(/\/products\/([^/?#]+)/);
    return m ? m[1] : null;
  } catch (e) {
    return null;
  }
}

/**
 * Shopify variant ids are positive integers; reject anything else.
 * @param {string | null} raw
 * @returns {string | null}
 */
function normalizeVariantId(raw) {
  return raw && /^\d+$/.test(raw) ? raw : null;
}

/** Locale path prefix (e.g. `/en`) when the store serves localized URLs. */
function localePrefix() {
  const m = location.pathname.match(/^(\/[a-z]{2}(?:-[a-z]{2})?)\/(?:collections|products)/i);
  return m ? m[1] : '';
}

/* --------------------------------- reorder -------------------------------- */

/**
 * Find the grid card matching `handle`. Custom collection tiles have no product
 * link and are skipped.
 * @param {Element} grid
 * @param {string} handle
 * @returns {HTMLElement | null}
 */
function findCardByHandle(grid, handle) {
  const items = grid.querySelectorAll(':scope > li');
  for (const li of items) {
    const link = li.querySelector(PRODUCT_LINK);
    if (link && handleFromHref(link.getAttribute('href')) === handle) {
      return /** @type {HTMLElement} */ (li);
    }
  }
  return null;
}

/**
 * Move an already-rendered card to the front of the grid.
 * @param {Element} grid
 * @param {HTMLElement} li
 */
function pinCard(grid, li) {
  if (grid.firstElementChild !== li) grid.prepend(li);
  markPinned(grid, li);
}

/**
 * Mark a card (reordered or freshly inserted at the front) as the pin and
 * normalize its `data-page`. paginated-list.js derives the previous-page number
 * from the first card's `data-page`, so the pin must report the first still-
 * visible real page — not a fetched card's source page.
 * @param {Element} grid
 * @param {HTMLElement} li
 */
function markPinned(grid, li) {
  li.dataset.curatedPin = 'true';
  const sibling = grid.querySelector(':scope > li[data-page]:not([data-curated-pin])');
  const firstPage = sibling instanceof HTMLElement ? sibling.dataset.page : undefined;
  if (firstPage != null) li.dataset.page = firstPage;
  else if (li.dataset.page != null) delete li.dataset.page;
}

/**
 * Build a shimmer placeholder shaped like a grid card, sized to a real card so
 * swapping in the fetched card causes no layout shift.
 * @param {Element} grid
 * @returns {HTMLElement}
 */
function buildSkeleton(grid) {
  injectSkeletonCss();
  const sample = grid.querySelector(':scope > li');
  const li = document.createElement('li');
  li.className = sample instanceof HTMLElement ? sample.className : 'product-grid__item';
  li.dataset.curatedPinSkeleton = 'true';
  li.setAttribute('aria-hidden', 'true');

  const box = document.createElement('div');
  box.className = 'curated-pin-skeleton';
  const height = sample instanceof HTMLElement ? sample.getBoundingClientRect().height : 0;
  if (height > 0) {
    box.style.minHeight = `${Math.round(height)}px`;
  } else {
    // Layout not measured yet → reserve height from the card image aspect-ratio
    // so the placeholder can't collapse to 0 and cause the shift it exists to prevent.
    const gallery = sample instanceof HTMLElement ? sample.querySelector('.card-gallery') : null;
    const ratio =
      gallery instanceof HTMLElement
        ? getComputedStyle(gallery).getPropertyValue('--gallery-aspect-ratio').trim()
        : '';
    box.style.aspectRatio = ratio || '1';
  }
  li.appendChild(box);
  return li;
}

let skeletonCssInjected = false;
function injectSkeletonCss() {
  if (skeletonCssInjected) return;
  skeletonCssInjected = true;
  const style = document.createElement('style');
  style.textContent =
    '.curated-pin-skeleton{width:100%;border-radius:var(--product-corner-radius,8px);' +
    'background:linear-gradient(100deg,rgba(0,0,0,.06) 30%,rgba(0,0,0,.12) 50%,rgba(0,0,0,.06) 70%);' +
    'background-size:200% 100%;animation:curated-pin-shimmer 1.2s ease-in-out infinite}' +
    '@keyframes curated-pin-shimmer{0%{background-position:200% 0}100%{background-position:-200% 0}}' +
    '@media(prefers-reduced-motion:reduce){.curated-pin-skeleton{animation:none}}';
  document.head.appendChild(style);
}

/* ----------------------------- fetch + prepend ---------------------------- */

/**
 * Fetch one product's card via the bare `?view=card` template (the
 * `templates/product.card.json` → `curated-pin-card` section) and return its
 * grid `<li>` adopted into the live document. One small request (~10-30KB), no
 * page lookup. Uses the `<head>` prefetch when it matches, so there's usually no
 * added round-trip. Returns null if the product/template is missing.
 * @param {string} handle
 * @param {string | null} variantId
 * @returns {Promise<HTMLElement | null>}
 */
async function fetchCardView(handle, variantId) {
  let html = null;

  // Reuse the card the <head> kicker already started fetching during page parse.
  const prefetch = /** @type {any} */ (window).__curatedPinPrefetch;
  if (prefetch && prefetch.handle === handle && (prefetch.variant || '') === (variantId || '')) {
    html = await prefetch.promise;
  }

  if (html == null) {
    const url = `${localePrefix()}/products/${encodeURIComponent(handle)}?view=card${
      variantId ? `&variant=${variantId}` : ''
    }`;
    try {
      const res = await fetch(url, { credentials: 'same-origin' });
      if (!res.ok) return null;
      html = await res.text();
    } catch (e) {
      return null;
    }
  }
  html = (html || '').trim();
  // A missing alternate template makes Shopify serve the full product page;
  // reject it so we never lift a related-product card off a PDP.
  if (!html || /^<!doctype/i.test(html) || /<html[\s>]/i.test(html)) return null;

  const tpl = document.createElement('template');
  tpl.innerHTML = html;
  const li = tpl.content.querySelector('li[ref="cards[]"]') || tpl.content.querySelector('li.product-grid__item');
  if (!li) return null;

  // Confirm the card is the product we asked for before inserting.
  const link = li.querySelector(PRODUCT_LINK);
  if (!link || handleFromHref(link.getAttribute('href')) !== handle) return null;
  return /** @type {HTMLElement} */ (document.importNode(li, true));
}

/**
 * Copy the live grid's card image-ratio onto a fetched card so its height
 * matches the surrounding cards (the bare template renders the block default).
 * @param {Element} grid
 * @param {HTMLElement} card
 */
function matchGridRatio(grid, card) {
  const liveGallery = grid.querySelector(':scope > li .card-gallery');
  const newGallery = card.querySelector('.card-gallery');
  if (!(liveGallery instanceof HTMLElement) || !(newGallery instanceof HTMLElement)) return;
  const ratio = getComputedStyle(liveGallery).getPropertyValue('--gallery-aspect-ratio').trim();
  if (ratio) newGallery.style.setProperty('--gallery-aspect-ratio', ratio);
}

/* ------------------------------ variant image ----------------------------- */

/**
 * Show the ad variant's color image and carry `?variant=` on the card link.
 * @param {HTMLElement} card
 * @param {string} handle
 * @param {string} variantId
 */
async function applyVariant(card, handle, variantId) {
  const productCard = card.querySelector('product-card');
  const mediaId = await resolveMediaId(card, handle, variantId);

  setVariantLink(card, variantId);

  if (!mediaId || !productCard) return; // size-only variant shares image → leave default
  await whenSlideshowReady(productCard);
  revealVariantSlide(productCard, mediaId);
}

/**
 * Map the full variant id to the media id of its color slide.
 *
 * Collection cards in this theme render no inline swatches, so the variant's
 * featured media id (from the product JSON) is the reliable source — it equals
 * the `slide-id` of the variant's slide in the card gallery. When a card does
 * expose a swatch input (other layouts), use it first and mark it selected.
 * @param {HTMLElement} card
 * @param {string} handle
 * @param {string} variantId
 * @returns {Promise<string | null>}
 */
async function resolveMediaId(card, handle, variantId) {
  // Inline swatch carrying the exact variant id (single-option products / layouts that show swatches).
  const direct = card.querySelector(`input[data-variant-id="${variantId}"][data-option-media-id]`);
  if (direct instanceof HTMLInputElement) {
    checkSwatch(direct);
    return direct.dataset.optionMediaId || null;
  }

  const product = await fetchProduct(handle);
  const variant = product && (product.variants || []).find((v) => String(v.id) === variantId);
  if (!variant) return null;

  // Best-effort: also mark the matching color swatch if one happens to be present.
  const swatches = card.querySelectorAll('input[name$="-swatch"][data-option-media-id]');
  for (const value of variant.options || []) {
    for (const input of swatches) {
      if (input instanceof HTMLInputElement && input.value === value) checkSwatch(input);
    }
  }

  // The variant's featured media id is the slide-id of its color image.
  return variant.featured_media && variant.featured_media.id != null ? String(variant.featured_media.id) : null;
}

/**
 * Reveal the slide for `mediaId` and hide other variant images — mirrors the
 * theme's own variant-image finalize without a section refetch or morph.
 *
 * Authoritative only while collection cards render no variant picker (current
 * layout). If card swatches are ever enabled, the card's own variant-image
 * handler runs on interaction and would compete with this toggling; drive the
 * picker instead of hand-toggling slides in that case.
 * @param {Element} productCard
 * @param {string} mediaId
 */
function revealVariantSlide(productCard, mediaId) {
  const slideshow = /** @type {any} */ (productCard).refs?.slideshow;
  const slides = slideshow?.refs?.slides || [];
  if (!slideshow || !slides.length) return;

  let target = null;
  for (const slide of slides) {
    if (slide.getAttribute('slide-id') === mediaId) {
      target = slide;
      slide.hidden = false;
      slide.setAttribute('aria-hidden', 'false');
    } else if (slide.hasAttribute('variant-image')) {
      slide.hidden = true;
      slide.setAttribute('aria-hidden', 'true');
    }
  }
  if (!target) return;

  try {
    slideshow.select({ id: mediaId }, undefined, { animate: false });
  } catch (e) {
    /* keep revealed slide even if select() is unavailable */
  }
}

/**
 * Set `?variant=` on every product link in the card.
 * @param {HTMLElement} card
 * @param {string} variantId
 */
function setVariantLink(card, variantId) {
  for (const link of card.querySelectorAll(PRODUCT_LINK)) {
    try {
      const url = new URL(/** @type {HTMLAnchorElement} */ (link).href, location.origin);
      url.searchParams.set('variant', variantId);
      /** @type {HTMLAnchorElement} */ (link).href = url.toString();
    } catch (e) {
      /* skip malformed href */
    }
  }
}

/** Visually mark the matching swatch without triggering the native refetch. */
function checkSwatch(input) {
  try {
    if (!input.checked) input.checked = true;
  } catch (e) {
    /* no-op */
  }
}

/**
 * @param {string} handle
 * @returns {Promise<any | null>}
 */
async function fetchProduct(handle) {
  if (handle in productCache) return productCache[handle];
  try {
    const res = await fetch(`${localePrefix()}/products/${handle}.js`, { credentials: 'same-origin' });
    const data = res.ok ? await res.json() : null;
    productCache[handle] = data;
    return data;
  } catch (e) {
    productCache[handle] = null;
    return null;
  }
}

/**
 * Resolve once the card's slideshow has rendered its slides.
 * @param {Element} productCard
 * @param {number} [timeout]
 * @returns {Promise<boolean>}
 */
function whenSlideshowReady(productCard, timeout = 3000) {
  return new Promise((resolve) => {
    const start = Date.now();
    (function poll() {
      if (/** @type {any} */ (productCard).refs?.slideshow?.refs?.slides?.length) return resolve(true);
      if (Date.now() - start > timeout) return resolve(false);
      requestAnimationFrame(poll);
    })();
  });
}

/* -------------------------------- dedup ----------------------------------- */

/**
 * Whether pagination could AJAX-insert cards later (infinite-scroll sentinels or
 * a results-list component). Arm dedup in either case — the observer is cheap and
 * only removes exact duplicates of the pinned product.
 */
function paginationMayInject() {
  return !!document.querySelector('results-list, [ref="viewMoreNext"], [ref="viewMorePrevious"]');
}

/**
 * Remove the natural-page copy of the pinned product when pagination appends it.
 * Only exact product duplicates are removed; sibling order is never changed.
 * @param {Element} grid
 * @param {string} handle
 * @param {HTMLElement} pinnedLi
 */
function watchForDuplicate(grid, handle, pinnedLi) {
  const observer = new MutationObserver((mutations) => {
    if (!mutations.some((m) => m.addedNodes.length)) return;
    for (const li of grid.querySelectorAll(':scope > li')) {
      if (li === pinnedLi) continue;
      const link = li.querySelector(PRODUCT_LINK);
      if (link && handleFromHref(link.getAttribute('href')) === handle) li.remove();
    }
  });
  observer.observe(grid, { childList: true });
}
